import React, { useState } from 'react'
import { OmDropdownMenu } from '@owma/kit'
import type { OmMenuItem, OmDropdownMenuConfig } from '@owma/kit'
import checkSVG from 'assets/images/check-mark-16.svg'
import SortAscIcon from 'assets/images/chevron-down-24.svg?react'
import sortSVG from 'assets/images/toolbar-more-24.svg'
import { useTranslation } from 'react-i18next'
import SVG from 'react-inlinesvg'
import { getSortOptions } from '../../../modules/TodoSort'
import { SortType } from '../../../modules/TodoFilter'
import styles from './style.module.scss'

interface TodoSortInfoListHeaderProps {
  currentFilter: string
  sortOption: SortType
  sortAscending?: boolean
  onSortDirectionToggle: () => void
  onSortChange: (sortKey: SortType) => void
}

const TodoSortInfoListHeader: React.FC<TodoSortInfoListHeaderProps> = ({
  currentFilter,
  sortOption,
  sortAscending = true,
  onSortDirectionToggle,
  onSortChange,
}) => {
  const { t } = useTranslation()
  const sortOptions = getSortOptions(currentFilter as any)
  const currentSortOption = sortOptions.find((option) => option.key === sortOption)
  const [sortDropdownOpen, setSortDropdownOpen] = useState<boolean>(false)

  if (!currentSortOption || !currentSortOption.label) {
    return null
  }

  const handleClick = () => {
    if (onSortDirectionToggle) {
      onSortDirectionToggle()
    }
  }

  const handleSortClick = (sortKey: SortType) => {
    if (onSortChange) {
      onSortChange(sortKey)
    }
    setSortDropdownOpen(false)
  }

  const renderSortMenu = (): OmDropdownMenuConfig | undefined => {
    const availableSortOptions = getSortOptions(currentFilter as any)
    const isDisabled = availableSortOptions.length === 0

    if (isDisabled) {
      return undefined
    }

    const menuItems: OmMenuItem[] = availableSortOptions.map((option) => ({
      key: option.key,
      label: (
        <div className={styles.sortMenuItem}>
          <span>{option.label}</span>
          {sortOption === option.key && (
            <SVG {...({ src: checkSVG, alt: 'check', className: styles.checkIcon } as any)} />
          )}
        </div>
      ),
    }))

    return {
      items: menuItems,
      onClick: ({ key }) => handleSortClick(key as SortType),
    }
  }

  const isSortDisabled = getSortOptions(currentFilter as any).length <= 1

  return (
    <div className={styles.sortHeader}>
      <div className={styles.sortHeaderInner}>
        <div className={styles.sortIconWrapper} onClick={handleClick}>
          <SortAscIcon
            className={`${styles.sortIcon} ${!sortAscending ? styles.sortIconDesc : ''}`}
          />
        </div>
        <div className={styles.sortText} onClick={handleClick}>
          {t('todo.todolist.sortBy', { sortBy: currentSortOption.label })}
        </div>

        <OmDropdownMenu
          menu={renderSortMenu()}
          trigger={['click']}
          placement="bottomLeft"
          overlayClassName={'contextMenu'}
          disabled={isSortDisabled}
          open={sortDropdownOpen}
          onOpenChange={setSortDropdownOpen}
        >
          <button
            className={`${styles.iconButton} ${isSortDisabled ? styles.disabled : ''}`}
            disabled={isSortDisabled}
            onClick={(e) => {
              e.stopPropagation()
              setSortDropdownOpen(!sortDropdownOpen)
            }}
          >
            <SVG {...({ src: sortSVG, alt: 'sort' } as any)} />
          </button>
        </OmDropdownMenu>
      </div>
    </div>
  )
}

export default TodoSortInfoListHeader
